import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, HelpCircle, Users, Brain, Activity, TrendingUp, Target, LayoutGrid, BarChart3, Zap, PenTool, Presentation, Globe, ArrowLeft, Printer, ChevronDown } from 'lucide-react';
interface StoryboardProps {
  isOpen: boolean;
  onClose: () => void;
  currentStep: number;
  onGoToStep: (step: number) => void;
  standalone?: boolean;
}
const PHASES = [{
  name: 'Icebreaker',
  color: '#6366f1',
  emoji: '🎯',
  steps: [{
    step: 1,
    label: 'Welcome',
    icon: Sparkles,
    description: 'Workshop intro & objectives'
  }, {
    step: 2,
    label: 'Quiz',
    icon: HelpCircle,
    description: 'Group icebreaker question'
  }]
}, {
  name: 'Deep Dive',
  color: '#0891b2',
  emoji: '🔬',
  steps: [{
    step: 3,
    label: 'Group Setup',
    icon: Users,
    description: 'Pick specialty & name your group'
  }, {
    step: 4,
    label: 'Specialty Mindset',
    icon: Brain,
    description: 'Beliefs → predict → reveal priorities'
  }, {
    step: 5,
    label: 'Treatment Behavior',
    icon: Activity,
    description: 'Predict how they treat & handle objections'
  }, {
    step: 6,
    label: 'Patient Staging',
    icon: Target,
    description: 'Place specialties on treatment timeline'
  }]
}, {
  name: 'Strategy',
  color: '#d97706',
  emoji: '⚡',
  steps: [{
    step: 7,
    label: 'Strategy Menu',
    icon: LayoutGrid,
    description: 'Choose your strategy exercise'
  }, {
    step: 8,
    label: 'Segmentation',
    icon: TrendingUp,
    description: 'Rank drivers & barriers'
  }, {
    step: 9,
    label: 'Arguments & Data',
    icon: BarChart3,
    description: 'Pick top features by effectiveness'
  }, {
    step: 10,
    label: 'Motivations',
    icon: Zap,
    description: 'Find the right framing'
  }]
}, {
  name: 'Share Back',
  color: '#059669',
  emoji: '🎤',
  steps: [{
    step: 11,
    label: 'Build Card',
    icon: PenTool,
    description: 'Write your group strategy'
  }, {
    step: 12,
    label: 'Present',
    icon: Presentation,
    description: 'Projector-ready strategy card'
  }, {
    step: 13,
    label: 'All Together',
    icon: Globe,
    description: 'Cross-specialty comparison'
  }]
}];
function StepCard({
  s,
  phaseColor,
  highlighted,
  isCurrent,
  isPast,
  onClick,
  delay
}: {
  s: {
    step: number;
    label: string;
    icon: React.ElementType;
    description: string;
  };
  phaseColor: string;
  highlighted: boolean;
  isCurrent: boolean;
  isPast: boolean;
  onClick: () => void;
  delay: number;
}) {
  const Icon = s.icon;
  return <motion.button initial={{
    opacity: 0,
    y: 16
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay,
    duration: 0.4,
    ease: 'easeOut'
  }} whileHover={{
    scale: 1.04,
    y: -2
  }} whileTap={{
    scale: 0.97
  }} onClick={onClick} className={`storyboard-step relative p-5 rounded-2xl border-2 text-left transition-all duration-200 group print:shadow-none print:border print:p-3 ${highlighted ? 'bg-white shadow-md hover:shadow-lg' : isCurrent ? 'bg-white shadow-lg' : isPast ? 'bg-gray-50 border-gray-100 hover:border-gray-200' : 'bg-white border-gray-100 hover:border-gray-200 opacity-60 hover:opacity-100'}`} style={{
    borderColor: highlighted || isCurrent ? phaseColor : undefined,
    boxShadow: highlighted ? `0 4px 20px ${phaseColor}15, 0 0 0 2px ${phaseColor}20` : isCurrent ? `0 0 0 3px ${phaseColor}20` : undefined
  }} data-id="element-592">
      <div className="flex items-center justify-between mb-3 print:mb-1.5" data-id="element-593">
        <div className="w-8 h-8 rounded-xl flex items-center justify-center text-xs font-bold print:w-6 print:h-6 print:rounded-md" style={{
        backgroundColor: highlighted || isCurrent ? phaseColor : `${phaseColor}15`,
        color: highlighted || isCurrent ? 'white' : phaseColor
      }} data-id="element-594">
          {s.step}
        </div>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center print:w-6 print:h-6" style={{
        backgroundColor: `${phaseColor}10`,
        color: highlighted || isCurrent ? phaseColor : `${phaseColor}80`
      }} data-id="element-595">
          <Icon size={16} data-id="element-596" />
        </div>
      </div>
      <h4 className={`font-bold text-sm print:text-xs ${highlighted || isCurrent ? 'text-dark' : isPast ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-597">
        {s.label}
      </h4>
      <p className={`text-xs mt-1 leading-relaxed print:text-[10px] print:mt-0.5 ${highlighted ? 'text-gray-500' : isCurrent ? 'text-gray-500' : 'text-gray-300'}`} data-id="element-598">
        {s.description}
      </p>
      {isCurrent && !highlighted && <motion.div className="absolute -top-1 -right-1 w-3 h-3 rounded-full print:hidden" style={{
      backgroundColor: phaseColor
    }} data-id="element-599">
          <motion.div className="w-full h-full rounded-full" style={{
        backgroundColor: phaseColor
      }} animate={{
        scale: [1, 1.5, 1],
        opacity: [1, 0.4, 1]
      }} transition={{
        duration: 2,
        repeat: Infinity
      }} data-id="element-600" />
        </motion.div>}
      {isPast && !highlighted && <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-green-500 text-white flex items-center justify-center text-xs print:hidden" data-id="element-601">
          ✓
        </div>}
    </motion.button>;
}
function OverlayStoryboard({
  onClose,
  currentStep,
  onGoToStep
}: {
  onClose: () => void;
  currentStep: number;
  onGoToStep: (step: number) => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-id="element-602">
      <motion.div initial={{
      opacity: 0,
      scale: 0.92,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.92,
      y: 20
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-603">
        <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-gray-100 rounded-t-3xl px-8 py-5 flex items-center justify-between z-10" data-id="element-604">
          <div data-id="element-605">
            <h2 className="text-2xl font-bold text-dark" data-id="element-606">
              Workshop Storyboard
            </h2>
            <p className="text-sm text-gray-400 mt-0.5" data-id="element-607">
              13 steps across 4 phases — click any step to jump there
            </p>
          </div>
          <button onClick={onClose} className="w-10 h-10 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors" aria-label="Close storyboard" data-id="element-608">
            <X size={20} className="text-gray-500" data-id="element-609" />
          </button>
        </div>
        <div className="px-8 py-6 space-y-8" data-id="element-610">
          {PHASES.map((phase, phaseIdx) => <div key={phase.name} data-id="element-611">
              <div className="flex items-center mb-4" data-id="element-612">
                <div className="w-3 h-3 rounded-full mr-3" style={{
              backgroundColor: phase.color
            }} data-id="element-613" />
                <h3 className="text-sm font-bold uppercase tracking-widest" style={{
              color: phase.color
            }} data-id="element-614">
                  {phase.name}
                </h3>
                <div className="flex-grow h-px bg-gray-100 ml-4" data-id="element-615" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-616">
                {phase.steps.map(s => {
              const Icon = s.icon;
              const isCurrent = currentStep === s.step;
              const isPast = currentStep > s.step;
              return <StepCard key={s.step} s={s} phaseColor={phase.color} highlighted={false} isCurrent={isCurrent} isPast={isPast} onClick={() => {
                onGoToStep(s.step);
                onClose();
              }} delay={0} data-id="element-617" />;
            })}
              </div>
              {phaseIdx < PHASES.length - 1 && <div className="flex justify-center mt-4" data-id="element-618">
                  <ChevronDown size={16} className="text-gray-200" data-id="element-619" />
                </div>}
            </div>)}
        </div>
        <div className="px-8 py-5 border-t border-gray-100 bg-gray-50/50 rounded-b-3xl" data-id="element-620">
          <p className="text-xs text-gray-400 text-center" data-id="element-621">
            You're on step {currentStep} of 13
          </p>
        </div>
      </motion.div>
    </motion.div>;
}
function StandaloneStoryboard({
  onClose,
  onGoToStep
}: {
  onClose: () => void;
  onGoToStep: (step: number) => void;
}) {
  return <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white print:bg-white print:min-h-0" id="storyboard-print" data-id="element-622">
      {/* Header — hidden in print */}
      <div className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-gray-100 print:hidden" data-id="element-623">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-624">
          <button onClick={onClose} className="flex items-center text-sm font-semibold text-gray-500 hover:text-dark transition-colors" data-id="element-625">
            <ArrowLeft size={18} className="mr-2" data-id="element-626" />
            Back to Workshop
          </button>
          <button onClick={() => window.print()} className="flex items-center text-sm font-semibold text-gray-500 hover:text-dark transition-colors bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg" data-id="element-627">
            <Printer size={16} className="mr-2" data-id="element-628" />
            Print / Save PDF
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12 print:px-0 print:py-4 print:max-w-none" data-id="element-629">
        {/* Title */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center mb-10 print:mb-6" data-id="element-630">
          <h1 className="text-4xl md:text-5xl font-bold text-dark mb-3 print:text-2xl print:mb-1" data-id="element-631">
            Workshop Storyboard
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto print:text-sm print:text-gray-500" data-id="element-632">
            Specialty Gap Explorer — 13 steps across 4 phases
          </p>
        </motion.div>

        {/* Phases */}
        <div className="space-y-10 print:space-y-4" data-id="element-633">
          {PHASES.map((phase, phaseIdx) => {
          let stepDelay = 0;
          for (let i = 0; i < phaseIdx; i++) stepDelay += PHASES[i].steps.length;
          return <motion.div key={phase.name} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: phaseIdx * 0.15,
            duration: 0.5
          }} className="print:break-inside-avoid" data-id="element-634">
                <div className="flex items-center mb-5 print:mb-2" data-id="element-635">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg mr-4 print:w-7 print:h-7 print:rounded-md print:text-sm print:mr-2" style={{
                backgroundColor: `${phase.color}12`
              }} data-id="element-636">
                    {phase.emoji}
                  </div>
                  <div data-id="element-637">
                    <h2 className="text-lg font-bold print:text-sm" style={{
                  color: phase.color
                }} data-id="element-638">
                      {phase.name}
                    </h2>
                    <p className="text-xs text-gray-400 print:hidden" data-id="element-639">
                      {phase.steps.length} step
                      {phase.steps.length > 1 ? 's' : ''}
                    </p>
                  </div>
                  <div className="flex-grow h-px ml-6 print:ml-3" style={{
                backgroundColor: `${phase.color}20`
              }} data-id="element-640" />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 print:grid-cols-4 print:gap-2" data-id="element-641">
                  {phase.steps.map((s, i) => <StepCard key={s.step} s={s} phaseColor={phase.color} highlighted={true} isCurrent={false} isPast={false} onClick={() => {
                onGoToStep(s.step);
                onClose();
              }} delay={0.08 * (stepDelay + i) + 0.2} data-id="element-642" />)}
                </div>

                {phaseIdx < PHASES.length - 1 && <div className="flex justify-center mt-5 print:mt-2" data-id="element-643">
                    <motion.div initial={{
                scaleY: 0
              }} animate={{
                scaleY: 1
              }} transition={{
                delay: (phaseIdx + 1) * 0.15 + 0.1,
                duration: 0.3
              }} className="w-px h-6 origin-top print:h-3" style={{
                backgroundColor: `${PHASES[phaseIdx + 1].color}30`
              }} data-id="element-644" />
                  </div>}
              </motion.div>;
        })}
        </div>

        {/* Footer */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.2,
        duration: 0.5
      }} className="text-center mt-14 pb-8 print:mt-6 print:pb-2" data-id="element-645">
          <p className="text-sm text-gray-300 print:text-xs print:text-gray-400" data-id="element-646">
            Specialty Gap Explorer — Interactive Workshop Tool
          </p>
        </motion.div>
      </div>
    </div>;
}
export function Storyboard({
  isOpen,
  onClose,
  currentStep,
  onGoToStep,
  standalone
}: StoryboardProps) {
  if (!isOpen) return null;
  if (standalone) {
    return <StandaloneStoryboard onClose={onClose} onGoToStep={onGoToStep} data-id="element-647" />;
  }
  return <AnimatePresence data-id="element-648">
      <OverlayStoryboard onClose={onClose} currentStep={currentStep} onGoToStep={onGoToStep} data-id="element-649" />
    </AnimatePresence>;
}